import { useWarnIfUnsavedChanges } from './UnsavedChange.hook';
import React, { ComponentProps, RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { PlcMessage } from '@components/PlcMessage/PlcMessage.component';
import { Validator } from './form.types';

// ! Order matters in the Validators, it must reflect the UI order
export type FormValidators<FormState> = {
  [key in keyof FormState]?: {
    method: (value: FormState[key], formData?: FormState) => Validator[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ref: RefObject<any> | undefined;
  };
};

export function useFormState<FormState extends {}, Key extends keyof FormState>({
  initialFormState,
  useNavigationWarn = false,
  validators,
  onNavigationCancel,
}: {
  initialFormState: FormState;
  validators?: FormValidators<FormState>;
  useNavigationWarn?: boolean;
  onNavigationCancel?: () => void;
}) {
  type ErrorState = { [key in Key]: boolean };

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(false);

  const [formData, setFormData] = useState(initialFormState);

  const [errorMessage, setErrorMessage] = useState<string>();
  const [submitCallbackErrorCode, setSubmitCallbackErrorCode] = useState<number>();
  const [successMessage, setSuccessMessage] = useState<string>();
  const [submitCallbackSuccess, setSubmitCallbackSuccess] = useState(false);
  const Message = (props: Omit<ComponentProps<typeof PlcMessage>, 'flavour'>) => {
    const generalMessage = errorMessage || successMessage;
    return generalMessage ? (
      <PlcMessage flavour={errorMessage ? 'red' : 'green'} {...props}>
        {generalMessage}
      </PlcMessage>
    ) : null;
  };
  const ErrorMessageBasedOnCode = (props: Omit<ComponentProps<typeof PlcMessage>, 'errorCode'>) =>
    submitCallbackErrorCode ? <PlcMessage errorCode={submitCallbackErrorCode} {...props} /> : null;

  const resetMessage = useCallback(() => {
    setErrorMessage(undefined);
    setSuccessMessage(undefined);
    setSubmitCallbackSuccess(false);
    setSubmitCallbackErrorCode(undefined);
  }, [setErrorMessage, setSuccessMessage]);

  function isFormDirty(): boolean {
    const allValidation = (Object.keys(formData) as Array<keyof FormState>).reduce<Array<'dirty' | 'clean'>>(
      (acc, cur) => {
        const condition = shallowEqual(formData[cur], initialFormState[cur]) ? 'clean' : 'dirty';
        return [...acc, condition];
      },
      []
    );
    return allValidation.includes('dirty');
  }

  const [isDirty, setIsDirty] = useState(false);
  useWarnIfUnsavedChanges(useNavigationWarn && isDirty, onNavigationCancel);

  useEffect(() => {
    setIsDirty(isFormDirty());
  }, [formData]);

  const errors = useMemo(() => {
    if (!validators) {
      return {} as ErrorState;
    }
    return (Object.keys(validators) as Key[]).reduce((acc, k) => {
      const value = formData[k];
      const fieldValidators = validators ? validators[k]?.method : undefined;
      acc[k] = fieldValidators ? !fieldValidators(value, formData).every(v => v.isValid) : false;
      return acc;
    }, {} as ErrorState);
  }, [formData, validators]);

  const isFormValid = useMemo(() => Object.values(errors).every(v => !v), [errors]);

  const disabledSubmitButton = useMemo(() => isFormLoading || !isFormValid || (isSubmitted && !isFormValid), [
    isFormLoading,
    isSubmitted,
    isFormValid,
  ]);

  function setFormField<K extends keyof FormState>(key: K, value: FormState[K]) {
    const newFormData = { ...formData };
    newFormData[key] = value;
    setFormData(newFormData);
  }

  function resetForm(newFormState?: FormState) {
    setFormData(newFormState || initialFormState);
    setIsSubmitted(false);
  }

  function scrollToFirstError() {
    const keysWithErrors = (Object.keys(errors) as Key[]).filter(k => errors[k]);
    if (keysWithErrors && keysWithErrors.length > 0) {
      const element = validators ? validators[keysWithErrors[0]]?.ref : undefined;
      if (element && element.current) {
        element.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }
  }

  const plcFormProps = {
    isFormLoading,
    isFormValid,
    resetMessage,
    scrollToFirstError,
    setIsDirty,
    setIsFormLoading,
    setIsSubmitted,
    setSubmitCallbackErrorCode,
    setSubmitCallbackSuccess,
  };
  return {
    ErrorMessageBasedOnCode,
    Message,
    disabledSubmitButton,
    errors,
    formData,
    isDirty,
    isSubmitted,
    plcFormProps,
    resetForm,
    setErrorMessage,
    setFormData,
    setFormField,
    setSuccessMessage,
    submitCallbackErrorCode,
    submitCallbackSuccess,
  };
}
